import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GraftonChat } from "@/components/GraftonChat";
import { useEmailData, type Email } from "@/hooks/useEmailData";
import { toast } from "sonner";
import {
  Mail, Inbox, Users, Clock, Star, StarOff, Folder, Plus, Trash2,
  Pencil, Check, X, Brain, Sparkles, ArrowLeft, Search, Loader2,
  Settings, MailOpen, Send, Bell, Eye, Phone, Building2, Heart,
  RefreshCw, Upload, AlertTriangle, ExternalLink, Reply, CornerUpLeft,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ── Types ──
interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  category: string;
  is_favorite: boolean;
  notes: string | null;
  created_at: string;
}

const contactCategoryLabels: Record<string, string> = {
  client: "Client",
  prospect: "Prospect",
  fournisseur: "Fournisseur",
  partenaire: "Partenaire",
};

const contactCategoryColors: Record<string, string> = {
  client: "bg-emerald-500/15 text-emerald-400",
  prospect: "bg-sky-500/15 text-sky-400",
  fournisseur: "bg-orange-500/15 text-orange-400",
  partenaire: "bg-violet-500/15 text-violet-400",
};

// ── Contacts Tab ──
const ContactsTab = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [importing, setImporting] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
      }
    } catch {
      toast.error("Erreur lors du chargement des contacts");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error("Fichier CSV vide ou invalide"); return; }
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/"/g, ""));
      const findCol = (...names: string[]) => headers.findIndex(h => names.some(n => h.includes(n)));
      const iFirst = findCol("first", "prénom", "prenom");
      const iLast = findCol("last", "nom");
      const iEmail = findCol("email", "mail");
      const iPhone = findCol("phone", "téléphone", "tel");
      const iCompany = findCol("company", "entreprise", "société");

      const rows = lines.slice(1);
      const batch = rows.map(row => {
        const cols: string[] = [];
        let cur = "", inQ = false;
        for (const ch of row) {
          if (ch === '"') inQ = !inQ;
          else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ""; }
          else cur += ch;
        }
        cols.push(cur.trim());
        return {
          first_name: (iFirst >= 0 ? cols[iFirst]?.replace(/"/g, "") : "") || "Contact",
          last_name: iLast >= 0 ? cols[iLast]?.replace(/"/g, "") || "" : "",
          email: iEmail >= 0 ? cols[iEmail]?.replace(/"/g, "") || null : null,
          phone: iPhone >= 0 ? cols[iPhone]?.replace(/"/g, "") || null : null,
          company: iCompany >= 0 ? cols[iCompany]?.replace(/"/g, "") || null : null,
          category: "prospect",
          notes: "Importé via CSV",
        };
      }).filter(r => r.first_name !== "Contact" || r.email);

      const res = await fetch(`${API_URL}/api/contacts/batch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts: batch }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.inserted} contacts importés`);
        fetchContacts();
      }
    } catch {
      toast.error("Erreur CSV");
    } finally {
      setImporting(false);
      if (e.target) e.target.value = "";
    }
  };

  const toggleFavorite = async (id: string, current: boolean) => {
    await fetch(`${API_URL}/api/contacts/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_favorite: !current }),
    });
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, is_favorite: !current } : c));
  };

  const filtered = contacts.filter((c) => {
    const matchSearch = !searchQuery ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = categoryFilter === "all" || c.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const categoryCounts = contacts.reduce((acc, c) => {
    acc[c.category] = (acc[c.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="hover:border-primary/30 transition-all" data-testid="contacts-total-card">
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 text-primary mx-auto mb-1" />
            <p className="text-2xl font-bold tabular-nums">{contacts.length}</p>
            <p className="text-xs text-muted-foreground">Total contacts</p>
          </CardContent>
        </Card>
        {Object.entries(categoryCounts).slice(0, 3).map(([cat, count]) => (
          <Card key={cat} className="hover:border-primary/30 transition-all">
            <CardContent className="p-4 text-center">
              <div className={`h-5 w-5 rounded-full mx-auto mb-1 ${
                cat === "client" ? "bg-emerald-400" : cat === "prospect" ? "bg-sky-400" : cat === "fournisseur" ? "bg-orange-400" : "bg-violet-400"
              }`} />
              <p className="text-2xl font-bold tabular-nums">{count}</p>
              <p className="text-xs text-muted-foreground">{contactCategoryLabels[cat] || cat}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher un contact..." className="pl-9" data-testid="contacts-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "client", "prospect", "fournisseur", "partenaire"].map((cat) => (
            <Button key={cat} variant={categoryFilter === cat ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter(cat)} data-testid={`contacts-filter-${cat}`}>
              {cat === "all" ? "Tous" : contactCategoryLabels[cat] || cat}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
          const input = document.createElement("input");
          input.type = "file"; input.accept = ".csv";
          input.onchange = (e) => handleCsvImport(e as any);
          input.click();
        }} disabled={importing} data-testid="contacts-import-csv-btn">
          <Upload className={`h-4 w-4 ${importing ? "animate-spin" : ""}`} />
          {importing ? "Import..." : "Importer CSV"}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/20 mb-3" />
              <p className="text-sm text-muted-foreground">
                {contacts.length === 0 ? "Aucun contact. Importez un fichier CSV ou ajoutez vos contacts." : "Aucun contact ne correspond."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/50 max-h-[500px] overflow-y-auto">
              {filtered.map((contact) => (
                <div key={contact.id} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`contact-row-${contact.id}`}>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-semibold text-primary">
                      {contact.first_name?.[0] || ""}{contact.last_name?.[0] || ""}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold truncate">{contact.first_name} {contact.last_name}</span>
                      <Badge className={`text-[10px] ${contactCategoryColors[contact.category] || "bg-muted text-muted-foreground"}`}>
                        {contactCategoryLabels[contact.category] || contact.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      {contact.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 shrink-0" /> {contact.email}</span>}
                      {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" /> {contact.phone}</span>}
                    </div>
                    {contact.company && <span className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-0.5"><Building2 className="h-3 w-3" /> {contact.company}</span>}
                  </div>
                  <button onClick={() => toggleFavorite(contact.id, contact.is_favorite)} className="shrink-0">
                    <Heart className={`h-4 w-4 transition-colors ${contact.is_favorite ? "text-red-400 fill-red-400" : "text-muted-foreground/30 hover:text-red-400"}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Not Connected State ──
const GmailNotConnected = () => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="h-16 w-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-6">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
      </div>
      <h2 className="text-xl font-bold mb-2">Gmail non connecté</h2>
      <p className="text-muted-foreground mb-6 max-w-md">
        Connectez votre compte Gmail dans les Connexions API pour voir vos emails ici.
      </p>
      <Button onClick={() => navigate("/parametres")} className="gap-2" data-testid="connect-gmail-btn">
        <ExternalLink className="h-4 w-4" /> Aller aux Connexions API
      </Button>
    </div>
  );
};

// ── Main Component ──
const EmailDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    folders, emails, drafts, loading, analysisActive, setAnalysisActive,
    gmailConnected, totalEmails,
    addFolder, updateFolder, deleteFolder,
    markAsRead, toggleStar,
    getEmailsByFolder, getCategoryStats,
    loadEmailsByFolder, getEmailDetail, reload,
  } = useEmailData(user?.id);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [fullEmail, setFullEmail] = useState<Email | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");

  // Auto-select INBOX on load
  useEffect(() => {
    if (!selectedFolderId && folders.length > 0) {
      setSelectedFolderId(folders[0].id);
    }
  }, [folders, selectedFolderId]);

  if (authLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!user) return null;

  const unreadCount = emails.filter((e) => !e.is_read).length;
  const activeFolder = selectedFolderId ? folders.find((f) => f.id === selectedFolderId) : null;

  const filteredEmails = emails.filter((e) =>
    !searchQuery ||
    e.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.sender_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.sender_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectFolder = async (folderId: string) => {
    setSelectedFolderId(folderId);
    setSelectedEmail(null);
    setFullEmail(null);
    await loadEmailsByFolder(folderId);
  };

  const handleSelectEmail = async (email: Email) => {
    setSelectedEmail(email);
    markAsRead(email.id);
    setLoadingDetail(true);
    const detail = await getEmailDetail(email.id, selectedFolderId || "INBOX");
    setFullEmail(detail || email);
    setLoadingDetail(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadEmailsByFolder(selectedFolderId || "INBOX");
    setRefreshing(false);
    toast.success("Emails actualisés");
  };

  const handleSendReply = async () => {
    if (!selectedEmail || !replyBody.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/gmail/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: selectedEmail.sender_email,
          subject: `Re: ${selectedEmail.subject}`,
          body: replyBody,
        }),
      });
      if (res.ok) {
        toast.success(`Réponse envoyée à ${selectedEmail.sender_name}`);
        setShowReply(false);
        setReplyBody("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur d'envoi");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  const handleSendCompose = async () => {
    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/gmail/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: composeTo,
          subject: composeSubject,
          body: composeBody,
        }),
      });
      if (res.ok) {
        toast.success(`Email envoyé à ${composeTo}`);
        setShowCompose(false);
        setComposeTo("");
        setComposeSubject("");
        setComposeBody("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur d'envoi");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "EEEE d MMMM yyyy à HH:mm", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const formatDateShort = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd/MM HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="email-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <Mail className="h-4 w-4 text-white" />
            </div>
            <div>
              <span className="font-semibold text-lg tracking-tight">E-mails & Communication</span>
              <p className="text-xs text-muted-foreground -mt-0.5">
                {gmailConnected ? `${totalEmails} emails dans votre boite` : "Gmail non connecté"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="gap-1.5" onClick={() => setShowCompose(true)} data-testid="compose-email-btn">
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">Nouveau message</span>
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleRefresh} disabled={refreshing} data-testid="email-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </Button>
            <Button variant={analysisActive ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setAnalysisActive(!analysisActive)} data-testid="email-analysis-btn">
              <Brain className="h-4 w-4" />
              <span className="hidden sm:inline">{analysisActive ? "Analyse active" : "Analyse auto"}</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="container py-8">
        <Tabs defaultValue="emails" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="emails" className="gap-2" data-testid="tab-emails">
              <Mail className="h-4 w-4" /> E-mails
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-2" data-testid="tab-contacts">
              <Users className="h-4 w-4" /> Contacts
            </TabsTrigger>
          </TabsList>

          {/* ═══ EMAILS TAB ═══ */}
          <TabsContent value="emails" className="space-y-8" data-testid="emails-tab-content">
            {!gmailConnected ? (
              <GmailNotConnected />
            ) : (
              <>
                {/* KPI Cards */}
                <ScrollReveal>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Card className="hover:border-primary/30 transition-all" data-testid="kpi-total-emails">
                      <CardContent className="p-4 text-center">
                        <Mail className="h-5 w-5 text-sky-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold tabular-nums">{totalEmails}</p>
                        <p className="text-xs text-muted-foreground">Total emails</p>
                      </CardContent>
                    </Card>
                    <Card className="hover:border-primary/30 transition-all" data-testid="kpi-displayed-emails">
                      <CardContent className="p-4 text-center">
                        <Inbox className="h-5 w-5 text-primary mx-auto mb-1" />
                        <p className="text-2xl font-bold tabular-nums">{emails.length}</p>
                        <p className="text-xs text-muted-foreground">Affichés</p>
                      </CardContent>
                    </Card>
                    <Card className="hover:border-primary/30 transition-all" data-testid="kpi-unread-emails">
                      <CardContent className="p-4 text-center">
                        <Eye className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold tabular-nums">{unreadCount}</p>
                        <p className="text-xs text-muted-foreground">Non lus</p>
                      </CardContent>
                    </Card>
                    <Card className="hover:border-primary/30 transition-all" data-testid="kpi-folders">
                      <CardContent className="p-4 text-center">
                        <Folder className="h-5 w-5 text-violet-500 mx-auto mb-1" />
                        <p className="text-2xl font-bold tabular-nums">{folders.length}</p>
                        <p className="text-xs text-muted-foreground">Dossiers</p>
                      </CardContent>
                    </Card>
                  </div>
                </ScrollReveal>

                {/* Folders + Emails */}
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Folder sidebar */}
                  <ScrollReveal>
                    <Card className="lg:col-span-1" data-testid="folders-panel">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Folder className="h-5 w-5 text-primary" />
                          Dossiers Gmail
                        </CardTitle>
                        <CardDescription>Vos dossiers synchronisés</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-1">
                        {loading && emails.length === 0 ? (
                          <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                        ) : (
                          folders.map((f) => {
                            const isActive = selectedFolderId === f.id;
                            return (
                              <button
                                key={f.id}
                                onClick={() => handleSelectFolder(f.id)}
                                data-testid={`folder-${f.id}`}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${isActive ? "bg-primary/10 text-primary font-medium border border-primary/20" : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"}`}
                              >
                                <Inbox className="h-4 w-4 shrink-0" style={{ color: f.color }} />
                                <span className="truncate flex-1 text-left">{f.name}</span>
                              </button>
                            );
                          })
                        )}
                      </CardContent>
                    </Card>
                  </ScrollReveal>

                  {/* Email list / detail */}
                  <ScrollReveal delay={100}>
                    <Card className="lg:col-span-2" data-testid="emails-panel">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {selectedEmail ? (
                              <>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedEmail(null); setFullEmail(null); }} data-testid="email-back-to-list">
                                  <ArrowLeft className="h-4 w-4" />
                                </Button>
                                <span className="truncate max-w-[300px]">{selectedEmail.subject}</span>
                              </>
                            ) : (
                              <>
                                <Inbox className="h-5 w-5 text-primary" />
                                {activeFolder?.name || "Sélectionnez un dossier"}
                                {emails.length > 0 && <Badge variant="secondary" className="text-xs ml-1">{filteredEmails.length}</Badge>}
                              </>
                            )}
                          </CardTitle>
                          {!selectedEmail && (
                            <div className="relative w-48">
                              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher..." className="pl-8 h-8 text-xs" data-testid="email-search" />
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {selectedEmail ? (
                          /* Email detail view */
                          <div className="space-y-4" data-testid="email-detail">
                            {loadingDetail ? (
                              <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                            ) : (
                              <>
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold">{selectedEmail.sender_name}</span>
                                      <span className="text-xs text-muted-foreground">&lt;{selectedEmail.sender_email}&gt;</span>
                                    </div>
                                    <span className="text-xs text-muted-foreground">{formatDate(selectedEmail.received_at)}</span>
                                  </div>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleStar(selectedEmail.id)} data-testid="email-toggle-star">
                                    {selectedEmail.is_starred ? <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> : <StarOff className="h-4 w-4 text-muted-foreground" />}
                                  </Button>
                                </div>
                                <div className="bg-muted/30 rounded-lg p-5 whitespace-pre-line text-sm leading-relaxed border border-border/50 min-h-[200px] max-h-[500px] overflow-y-auto" data-testid="email-body">
                                  {fullEmail?.body || selectedEmail.body || selectedEmail.preview}
                                </div>
                                {/* Reply section */}
                                {!showReply ? (
                                  <div className="flex gap-2">
                                    <Button size="sm" className="gap-1.5" onClick={() => setShowReply(true)} data-testid="email-reply-btn">
                                      <CornerUpLeft className="h-4 w-4" /> Répondre
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="space-y-3 border border-primary/20 rounded-lg p-4 bg-primary/[0.02]" data-testid="email-reply-form">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      <CornerUpLeft className="h-4 w-4" />
                                      <span>Répondre à <strong className="text-foreground">{selectedEmail.sender_name}</strong> &lt;{selectedEmail.sender_email}&gt;</span>
                                    </div>
                                    <Textarea
                                      value={replyBody}
                                      onChange={(e) => setReplyBody(e.target.value)}
                                      placeholder="Écrivez votre réponse..."
                                      rows={5}
                                      className="resize-none"
                                      data-testid="email-reply-textarea"
                                      autoFocus
                                    />
                                    <div className="flex gap-2 justify-end">
                                      <Button variant="outline" size="sm" onClick={() => { setShowReply(false); setReplyBody(""); }} data-testid="email-reply-cancel">
                                        Annuler
                                      </Button>
                                      <Button size="sm" className="gap-1.5" onClick={handleSendReply} disabled={sending || !replyBody.trim()} data-testid="email-reply-send">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        {sending ? "Envoi..." : "Envoyer"}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : loading ? (
                          <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                        ) : filteredEmails.length === 0 ? (
                          <div className="flex flex-col items-center justify-center p-12 text-center">
                            <MailOpen className="h-12 w-12 text-muted-foreground/20 mb-3" />
                            <p className="text-sm text-muted-foreground">
                              {emails.length === 0 ? "Aucun email dans ce dossier" : "Aucun résultat pour cette recherche"}
                            </p>
                          </div>
                        ) : (
                          /* Email list */
                          <div className="divide-y divide-border/50 max-h-[500px] overflow-y-auto rounded-lg border border-border/50" data-testid="email-list">
                            {filteredEmails.map((email) => (
                              <button
                                key={email.id}
                                onClick={() => handleSelectEmail(email)}
                                data-testid={`email-row-${email.id}`}
                                className={`w-full text-left px-4 py-3 hover:bg-muted/30 transition-colors flex items-start gap-3 ${!email.is_read ? "bg-primary/[0.03]" : ""}`}
                              >
                                <div className="pt-0.5">
                                  <span role="button" onClick={(e) => { e.stopPropagation(); toggleStar(email.id); }}>
                                    {email.is_starred ? <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> : <Star className="h-4 w-4 text-muted-foreground/20 hover:text-muted-foreground/50" />}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className={`text-sm truncate ${!email.is_read ? "font-semibold" : ""}`}>{email.sender_name}</span>
                                    <span className="text-[10px] text-muted-foreground shrink-0">{formatDateShort(email.received_at)}</span>
                                  </div>
                                  <p className={`text-sm truncate ${!email.is_read ? "font-medium text-foreground" : "text-muted-foreground"}`}>{email.subject}</p>
                                  <p className="text-xs text-muted-foreground truncate">{email.preview}</p>
                                </div>
                                {!email.is_read && <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-2" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </ScrollReveal>
                </div>

                {/* Grafton Chat */}
                <ScrollReveal>
                  <Card>
                    <CardContent className="p-0">
                      <GraftonChat
                        context={{
                          totalEmails: totalEmails,
                          unreadEmails: unreadCount,
                          pendingDrafts: 0,
                          categories: getCategoryStats(),
                        }}
                        subtitle="Assistant email"
                        quickPrompts={[
                          "Analyse mes emails non lus et donne-moi un résumé",
                          "Quels emails nécessitent une action urgente ?",
                          "Rédige une réponse professionnelle pour le dernier devis reçu",
                          "Classe mes emails par priorité",
                        ]}
                      />
                    </CardContent>
                  </Card>
                </ScrollReveal>
              </>
            )}
          </TabsContent>

          {/* ═══ CONTACTS TAB ═══ */}
          <TabsContent value="contacts" data-testid="contacts-tab-content">
            <ContactsTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Compose Email Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" /> Nouveau message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="Destinataire (email)"
              value={composeTo}
              onChange={(e) => setComposeTo(e.target.value)}
              data-testid="compose-to"
            />
            <Input
              placeholder="Objet"
              value={composeSubject}
              onChange={(e) => setComposeSubject(e.target.value)}
              data-testid="compose-subject"
            />
            <Textarea
              placeholder="Votre message..."
              rows={8}
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              data-testid="compose-body"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCompose(false)}>
                Annuler
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={handleSendCompose}
                disabled={sending || !composeTo.trim() || !composeSubject.trim() || !composeBody.trim()}
                data-testid="compose-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailDashboard;
